'use client';

import { nb } from '@/lib/nobreak';
import { useState } from 'react';
import { COST } from './cost-sources';

/**
 * Same budget, how many funded loans — and, only if the visitor supplies it,
 * how much revenue.
 *
 * Every number is the budget divided by a PUBLISHED cost-per-funded-loan range
 * from the cost table on this page (LEAD-COST-ECONOMICS-SOURCE-OF-TRUTH.md).
 * Revenue per loan is the visitor's own figure and starts EMPTY: the page never
 * shows a dollar return it would have had to invent.
 */

type Source = { key: string; label: string; note: string; lo: number; hi: number | null };
const SOURCES: Source[] = [
  { key: 'shared', label: 'Shared marketplace leads', note: `LendingTree-type · ${COST.shared.cpflLabel} per funded loan`, lo: COST.shared.cpfl[0], hi: COST.shared.cpfl[1] },
  { key: 'rate', label: 'Rate-table leads', note: `Bankrate-type · ${COST.rate.cpflLabel} per funded loan`, lo: COST.rate.cpfl[0], hi: COST.rate.cpfl[1] },
  { key: 'vendor', label: 'Vendor “exclusive” leads', note: `Sold to one buyer · ${COST.vendor.cpflLabel} per funded loan`, lo: COST.vendor.cpfl[0], hi: COST.vendor.cpfl[1] },
];
const OURS = { lo: COST.ours.cpfl[0], hi: COST.ours.cpfl[1] as number };

const f1 = (n: number) => (n >= 10 ? Math.round(n).toLocaleString('en-US') : n.toFixed(1).replace(/\.0$/, ''));
const money = (n: number) => '$' + Math.round(n).toLocaleString('en-US');

/** A funded loan as a house; a fractional loan fills that fraction of the icon. */
function Houses({ lo, hi, ours }: { lo: number; hi: number; ours: boolean }) {
  const n = Math.min(24, Math.max(1, Math.ceil(hi)));
  return (
    <div className="flex flex-wrap gap-1.5" aria-hidden>
      {Array.from({ length: n }).map((_, i) => {
        const solid = Math.max(0, Math.min(1, lo - i));   // guaranteed at the low end
        const maybe = Math.max(0, Math.min(1, hi - i));   // reachable at the high end
        return (
          <span key={i} className="relative h-8 w-8 sm:h-9 sm:w-9">
            <svg viewBox="0 0 24 24" className="absolute inset-0 h-full w-full" fill="none" stroke={ours ? 'rgba(255,124,198,.55)' : 'rgba(255,255,255,.28)'} strokeWidth="1.6" strokeDasharray={maybe > 0 && solid < 1 ? '2.5 2' : undefined}>
              <path d="M3.5 11 12 4l8.5 7v8.5a1 1 0 0 1-1 1H14v-5.5h-4v5.5H4.5a1 1 0 0 1-1-1V11Z" strokeLinejoin="round" />
            </svg>
            {solid > 0 && (
              <span className="absolute inset-0 overflow-hidden" style={{ width: `${solid * 100}%` }}>
                <svg viewBox="0 0 24 24" className="h-8 w-8 sm:h-9 sm:w-9" fill={ours ? 'url(#lproi-g)' : 'rgba(255,255,255,.55)'}>
                  <path d="M3.5 11 12 4l8.5 7v8.5a1 1 0 0 1-1 1H14v-5.5h-4v5.5H4.5a1 1 0 0 1-1-1V11Z" />
                </svg>
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
}

export function LPRoi({ href = '/mortgage-marketing-roi-calculator', demoHref = '/mortgage-lead-generation-demo' }: { href?: string; demoHref?: string }) {
  const [budget, setBudget] = useState(5000);
  const [srcKey, setSrcKey] = useState('shared');
  const [rev, setRev] = useState('');
  const src = SOURCES.find((s) => s.key === srcKey)!;

  const them = { lo: src.hi ? budget / src.hi : 0, hi: budget / src.lo };
  const ours = { lo: budget / OURS.hi, hi: budget / OURS.lo };
  // An open-ended source ("$2,000+") is compared at its BEST case, $2,000 — never against zero,
  // which would have claimed up to +120 loans a year where +48 is the honest ceiling.
  const extra = src.hi
    ? { lo: Math.max(0, ours.lo - them.hi) * 12, hi: (ours.hi - them.lo) * 12 }
    : { lo: 0, hi: Math.max(0, ours.hi - them.hi) * 12 };
  const revenue = Number(rev.replace(/[^0-9.]/g, ''));
  const pct = ((budget - 1000) / (25000 - 1000)) * 100;
  const themText = src.hi ? `${f1(them.lo)}–${f1(them.hi)}` : `up to ${f1(them.hi)}`;

  return (
    <div className="lp-glow relative overflow-hidden rounded-[32px] bg-[#0B0C12] text-white">
      <svg width="0" height="0" className="absolute"><defs><linearGradient id="lproi-g" x1="0" x2="1" y1="0" y2="1"><stop stopColor="#ff34a7" /><stop offset="1" stopColor="#6d5cff" /></linearGradient></defs></svg>

      <div className="grid lg:grid-cols-[.92fr_1.08fr]">
        {/* ── inputs ─────────────────────────────────────────────────── */}
        <div className="border-b border-white/10 p-6 sm:p-10 lg:border-b-0 lg:border-r lg:p-12">
          <p className="lp-eyebrow text-[#ff7cc6]">Live calculator</p>
          <h2 className="mt-4 text-[2.3rem] font-semibold leading-[1.02] sm:text-[3rem]">
            Calculate Your Mortgage Leads <span className="lp-accent lp-grad">ROI</span>
          </h2>
          <p className="mt-4 max-w-[30rem] text-[1.05rem] leading-relaxed text-white/65">
            See how many more loans you&apos;d close with exclusive vs shared mortgage leads.
          </p>

          <p className="mt-10 text-[12px] uppercase tracking-[.14em] text-white/55">1 · What you buy today</p>
          <div className="mt-3 grid gap-2">
            {SOURCES.map((s) => (
              <button key={s.key} type="button" onClick={() => setSrcKey(s.key)} aria-pressed={s.key === srcKey}
                className={`rounded-2xl px-4 py-3 text-left transition ${s.key === srcKey ? 'bg-white text-[#0B0C12] shadow-[0_10px_30px_-10px_rgba(255,52,167,.5)]' : 'bg-white/[.05] text-white ring-1 ring-white/10 hover:bg-white/[.09]'}`}>
                <span className="block text-[15px] font-semibold">{s.label}</span>
                <span className={`block text-[12.5px] ${s.key === srcKey ? 'text-[#6B6F7A]' : 'text-white/55'}`}>{s.note}</span>
              </button>
            ))}
          </div>

          <div className="mt-9 flex flex-wrap items-end justify-between gap-x-4 gap-y-1">
            <label htmlFor="lp-budget" className="text-[12px] uppercase tracking-[.14em] text-white/55">2 · Monthly lead budget</label>
            <span className="lp-num text-[2.6rem]" aria-live="polite">{money(budget)}</span>
          </div>
          <input id="lp-budget" type="range" min={1000} max={25000} step={500} value={budget}
            onChange={(e) => setBudget(Number(e.target.value))}
            className="mt-4 h-2 w-full cursor-pointer appearance-none rounded-full outline-none [&::-moz-range-thumb]:h-7 [&::-moz-range-thumb]:w-7 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-white [&::-webkit-slider-thumb]:h-7 [&::-webkit-slider-thumb]:w-7 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-[0_0_0_6px_rgba(255,52,167,.35)]"
            style={{ background: `linear-gradient(90deg,#ff34a7 0%,#b43cf0 ${pct / 2}%,#294bfd ${pct}%,rgba(255,255,255,.14) ${pct}%)` }} />
          <div className="mt-2 flex justify-between text-[12px] text-white/55"><span>$1,000</span><span>$25,000</span></div>

          <label htmlFor="lp-rev" className="mt-9 block text-[12px] uppercase tracking-[.14em] text-white/55">3 · Optional — your revenue per funded loan</label>
          <div className="mt-3 flex items-center rounded-2xl bg-white/[.05] px-4 ring-1 ring-white/10 focus-within:ring-[#ff34a7]/60">
            <span className="text-white/55">$</span>
            <input id="lp-rev" inputMode="numeric" placeholder="Enter yours to see revenue" value={rev}
              onChange={(e) => setRev(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
              className="h-12 w-full bg-transparent px-2 text-[1rem] text-white placeholder:text-white/50 focus:outline-none" />
          </div>
        </div>

        {/* ── results ────────────────────────────────────────────────── */}
        <div className="p-6 sm:p-10 lg:p-12">
          <p className="text-[12px] uppercase tracking-[.14em] text-white/55">Funded loans per month, same {money(budget)}</p>

          <div className="mt-6 rounded-3xl bg-white/[.04] p-5 ring-1 ring-white/10 sm:p-6">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <span className="text-[15px] text-white/65">{src.label}</span>
              <span className="lp-num whitespace-nowrap text-[2rem] text-white/60">{themText}</span>
            </div>
            <div className="mt-4"><Houses lo={them.lo} hi={them.hi} ours={false} /></div>
          </div>

          <div className="mt-3 rounded-3xl bg-gradient-to-br from-[#ff34a7]/[.16] to-[#294bfd]/[.14] p-5 ring-1 ring-[#ff34a7]/35 sm:p-6">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <span className="text-[15px] font-semibold text-white">Generating your own</span>
              <span className="lp-num lp-grad whitespace-nowrap text-[2.6rem]">{f1(ours.lo)}–{f1(ours.hi)}</span>
            </div>
            <div className="mt-4"><Houses lo={ours.lo} hi={ours.hi} ours /></div>
          </div>

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div className="rounded-3xl bg-white p-5 text-[#0B0C12] sm:p-6">
              <p className="text-[12px] uppercase tracking-[.14em] text-[#6B6F7A]">Extra funded loans a year</p>
              <p className="lp-num mt-2 text-[2.4rem]"><span className="lp-grad">{extra.lo > 0 ? `+${f1(extra.lo)}–${f1(extra.hi)}` : `up to +${f1(extra.hi)}`}</span></p>
              <p className="text-[13px] text-[#6B6F7A]">from the same spend</p>
            </div>
            <div className="rounded-3xl bg-white/[.05] p-5 ring-1 ring-white/10 sm:p-6">
              {revenue > 0 ? (
                <>
                  <p className="text-[12px] uppercase tracking-[.14em] text-white/55">Extra revenue a year</p>
                  <p className="lp-num lp-grad mt-2 text-[2.4rem]">{extra.lo > 0 ? `${money(extra.lo * revenue)}+` : `up to ${money(extra.hi * revenue)}`}</p>
                  <p className="text-[13px] text-white/50">at your {money(revenue)} per loan{extra.lo > 0 && `, up to ${money(extra.hi * revenue)}`}</p>
                </>
              ) : (
                <>
                  <p className="text-[12px] uppercase tracking-[.14em] text-white/55">Extra revenue a year</p>
                  <p className="mt-3 text-[15px] leading-snug text-white/60">Add your revenue per funded loan in step 3 to see it. We don&apos;t guess it for you.</p>
                </>
              )}
            </div>
          </div>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <a href={demoHref} className="lp-shimmer inline-flex h-14 items-center justify-center gap-2.5 whitespace-nowrap rounded-full bg-[#ff34a7] px-7 text-[1.0625rem] font-semibold text-white shadow-[0_14px_34px_-10px_rgba(255,52,167,.75)] transition hover:bg-[#f41f9b]">
              <span>Get this plan for my market</span>
              <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 10h11M11 5l5 5-5 5" strokeLinecap="round" /></svg>
            </a>
            <a href={href} className="inline-flex h-14 items-center justify-center rounded-full px-5 text-[15px] font-semibold text-white/80 ring-1 ring-white/20 transition hover:bg-white/10">Calculate My ROI in full</a>
          </div>
          <p className="mt-4 text-[12px] leading-snug text-white/55">{nb(`Takes 2 minutes. Shockingly clear results. Math: budget ÷ the published cost per funded loan for each source; yours is the blended first-party ${COST.ours.cpflLabel}. Results depend on speed to lead and follow-up.`)}</p>
        </div>
      </div>
    </div>
  );
}
